<template>
  <div class="modal-overlay" @click="handleOverlayClick">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <h3><i class="fa fa-upload"></i> Upload File</h3>
        <button class="close-btn" @click="$emit('cancel')">
          <i class="fa fa-times"></i>
        </button>
      </div>
      
      <div class="modal-body">
        <!-- File Upload Area -->
        <div 
          class="upload-area"
          :class="{ 'dragover': isDragOver, 'has-file': selectedFile }"
          @drop="handleDrop"
          @dragover="handleDragOver"
          @dragenter="handleDragEnter"
          @dragleave="handleDragLeave"
          @click="triggerFileInput"
        >
          <input
            ref="fileInput"
            type="file"
            @change="handleFileSelect"
            accept="image/*,application/pdf,.doc,.docx,.txt"
            style="display: none"
          />
          
          <div v-if="!selectedFile" class="upload-prompt">
            <i class="fa fa-cloud-upload"></i>
            <h4>Drop files here or click to browse</h4>
            <p>Supported formats: Images, PDF, DOC, TXT</p>
            <div class="file-limits">
              <small>Max size: 5MB for SMS, 16MB for WhatsApp</small>
            </div>
          </div>
          
          <div v-else class="file-preview">
            <div class="file-icon">
              <i class="fa" :class="getFileIcon(selectedFile)"></i>
            </div>
            <div class="file-info">
              <div class="file-name">{{ selectedFile.name }}</div>
              <div class="file-size">{{ formatFileSize(selectedFile.size) }}</div>
              <div class="file-type">{{ selectedFile.type || 'Unknown type' }}</div>
            </div>
            <button class="remove-file" @click.stop="removeFile">
              <i class="fa fa-times"></i>
            </button>
          </div>
        </div>
        
        <!-- File Validation Messages -->
        <div v-if="validationMessage" class="validation-message" :class="validationClass">
          <i class="fa" :class="validationClass === 'error' ? 'fa-exclamation-triangle' : 'fa-info-circle'"></i>
          {{ validationMessage }}
        </div>
        
        <!-- Upload Progress -->
        <div v-if="uploading" class="upload-progress">
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: uploadProgress + '%' }"></div>
          </div>
          <div class="progress-text">Uploading... {{ uploadProgress }}%</div>
        </div>
      </div>
      
      <div class="modal-footer">
        <button 
          class="btn btn-primary"
          @click="uploadFile"
          :disabled="!selectedFile || !isValidFile || uploading"
        >
          <i class="fa" :class="uploading ? 'fa-spinner fa-spin' : 'fa-upload'"></i>
          {{ uploading ? 'Uploading...' : 'Send File' }}
        </button>
        <button class="btn btn-secondary" @click="$emit('cancel')" :disabled="uploading">
          Cancel
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { compressImageSimple, isCompressibleImage } from "@/utils/imageCompression";
import { validateFile as validateFileUpload } from "@/utils/fileUpload";

export default {
  name: 'FileUploadModal',
  data() {
    return {
      selectedFile: null,
      isDragOver: false,
      uploading: false,
      uploadProgress: 0,
      validationMessage: '',
      validationClass: '',
      compressing: false
    }
  },
  computed: {
    isValidFile() {
      if (!this.selectedFile) return false
      // SMS has a 5MB limit (Twilio constraint) — override all categories
      const smsOverrides = {
        image: { maxSize: 5 * 1024 * 1024 },
        document: { maxSize: 5 * 1024 * 1024 },
        video: { maxSize: 5 * 1024 * 1024 },
        audio: { maxSize: 5 * 1024 * 1024 }
      }
      const result = validateFileUpload(this.selectedFile, smsOverrides)
      return result.valid
    }
  },
  
  watch: {
    selectedFile: {
      handler(newFile) {
        this.validateFile(newFile)
      },
      immediate: true
    }
  },
  methods: {
    validateFile(file) {
      if (!file) {
        this.validationMessage = ''
        this.validationClass = ''
        return
      }

      // SMS has a 5MB limit (Twilio constraint)
      const smsOverrides = {
        image: { maxSize: 5 * 1024 * 1024 },
        document: { maxSize: 5 * 1024 * 1024 },
        video: { maxSize: 5 * 1024 * 1024 },
        audio: { maxSize: 5 * 1024 * 1024 }
      }
      const result = validateFileUpload(file, smsOverrides)

      if (!result.valid) {
        this.validationMessage = result.error
        this.validationClass = 'error'
        return
      }

      this.validationMessage = 'File ready to send'
      this.validationClass = 'success'
    },
    
    triggerFileInput() {
      if (this.uploading) return
      this.$refs.fileInput.click()
    },
    
    async handleFileSelect(event) {
      const file = event.target.files[0]
      if (file) {
        await this.processFile(file)
      }
    },

    async processFile(file) {
      // Compress images before setting as selected file
      if (isCompressibleImage(file)) {
        try {
          this.compressing = true
          this.validationMessage = 'Optimizing image...'
          this.validationClass = 'warning'
          const compressedFile = await compressImageSimple(file)
          this.selectedFile = compressedFile
          console.log(`Image compressed: ${(file.size / 1024).toFixed(0)}KB -> ${(compressedFile.size / 1024).toFixed(0)}KB`)
        } catch (error) {
          console.error('Compression failed, using original:', error)
          this.selectedFile = file
        } finally {
          this.compressing = false
        }
      } else {
        this.selectedFile = file
      }
    },
    
    async handleDrop(event) {
      event.preventDefault()
      this.isDragOver = false

      if (this.uploading) return

      const files = event.dataTransfer.files
      if (files.length > 0) {
        await this.processFile(files[0])
      }
    },
    
    handleDragOver(event) {
      event.preventDefault()
      this.isDragOver = true
    },
    
    handleDragEnter(event) {
      event.preventDefault()
      this.isDragOver = true
    },
    
    handleDragLeave(event) {
      event.preventDefault()
      this.isDragOver = false
    },
    
    removeFile() {
      if (this.uploading) return
      this.selectedFile = null
      this.validationMessage = ''
      this.validationClass = ''
    },
    
    async uploadFile() {
      if (!this.selectedFile || !this.isValidFile || this.uploading) return
      
      try {
        this.uploading = true
        this.uploadProgress = 0
        
        // Convert file to base64 for sending
        const base64 = await this.fileToBase64(this.selectedFile)
        
        // Simulate upload progress
        const progressInterval = setInterval(() => {
          this.uploadProgress += 10
          if (this.uploadProgress >= 90) {
            clearInterval(progressInterval)
          }
        }, 100)
        
        // Prepare file data
        const fileData = {
          name: this.selectedFile.name,
          type: this.selectedFile.type,
          size: this.selectedFile.size,
          base64: base64
        }
        
        this.uploadProgress = 100
        
        // Emit the file data
        this.$emit('send', fileData)
        
      } catch (error) {
        console.error('File upload error:', error)
        this.validationMessage = 'Upload failed. Please try again.'
        this.validationClass = 'error'
      } finally {
        this.uploading = false
        this.uploadProgress = 0
      }
    },
    
    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => resolve(reader.result)
        reader.onerror = error => reject(error)
      })
    },
    
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'
      
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },
    
    getFileIcon(file) {
      if (!file.type) return 'fa-file-o'
      
      if (file.type.startsWith('image/')) return 'fa-file-image-o'
      if (file.type === 'application/pdf') return 'fa-file-pdf-o'
      if (file.type.includes('word')) return 'fa-file-word-o'
      if (file.type === 'text/plain') return 'fa-file-text-o'
      
      return 'fa-file-o'
    },
    
    handleOverlayClick() {
      if (!this.uploading) {
        this.$emit('cancel')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 500px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
  
  h3 {
    margin: 0;
    color: #333;
    font-size: 18px;
    
    .fa {
      margin-right: 8px;
      color: #ff9800;
    }
  }
  
  .close-btn {
    width: 32px;
    height: 32px;
    border: none;
    background: transparent;
    color: #666;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
      background: #f5f5f5;
    }
  }
}

.modal-body {
  padding: 20px;
}

.upload-area {
  border: 2px dashed #ddd;
  border-radius: 12px;
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #ff9800;
    background: #fff8f0;
  }
  
  &.dragover {
    border-color: #ff9800;
    background: #fff8f0;
    transform: scale(1.02);
  }
  
  &.has-file {
    border-color: #4caf50;
    background: #f8fff8;
    cursor: default;
  }
  
  .upload-prompt {
    .fa {
      font-size: 48px;
      color: #ff9800;
      margin-bottom: 16px;
    }
    
    h4 {
      margin: 0 0 8px 0;
      color: #333;
      font-weight: 600;
    }
    
    p {
      margin: 0 0 12px 0;
      color: #666;
      font-size: 14px;
    }
    
    .file-limits {
      small {
        color: #999;
        font-size: 12px;
      }
    }
  }
  
  .file-preview {
    display: flex;
    align-items: center;
    text-align: left;
    position: relative;
    
    .file-icon {
      width: 60px;
      height: 60px;
      background: #f0f0f0;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 16px;
      
      .fa {
        font-size: 24px;
        color: #666;
      }
    }
    
    .file-info {
      flex: 1;
      
      .file-name {
        font-weight: 600;
        color: #333;
        margin-bottom: 4px;
        word-break: break-word;
      }
      
      .file-size {
        color: #666;
        font-size: 13px;
        margin-bottom: 2px;
      }
      
      .file-type {
        color: #999;
        font-size: 12px;
      }
    }
    
    .remove-file {
      position: absolute;
      top: -8px;
      right: -8px;
      width: 24px;
      height: 24px;
      border: none;
      background: #f44336;
      color: white;
      border-radius: 50%;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      
      &:hover {
        background: #d32f2f;
      }
    }
  }
}

.validation-message {
  margin-top: 16px;
  padding: 12px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  font-size: 14px;
  
  .fa {
    margin-right: 8px;
  }
  
  &.success {
    background: #e8f5e8;
    color: #4caf50;
    border: 1px solid #c8e6c9;
  }
  
  &.error {
    background: #ffebee;
    color: #f44336;
    border: 1px solid #ffcdd2;
  }
  
  &.warning {
    background: #fff8e1;
    color: #ff9800;
    border: 1px solid #ffecb3;
  }
}

.upload-progress {
  margin-top: 16px;
  
  .progress-bar {
    width: 100%;
    height: 6px;
    background: #f0f0f0;
    border-radius: 3px;
    overflow: hidden;
    margin-bottom: 8px;
    
    .progress-fill {
      height: 100%;
      background: #ff9800;
      transition: width 0.3s ease;
    }
  }
  
  .progress-text {
    text-align: center;
    font-size: 13px;
    color: #666;
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px;
  border-top: 1px solid #e0e0e0;
  
  .btn {
    padding: 10px 20px;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: all 0.2s ease;
    
    &.btn-primary {
      background: #ff9800;
      color: white;
      
      &:hover:not(:disabled) {
        background: #f57c00;
      }
      
      &:disabled {
        background: #ccc;
        cursor: not-allowed;
      }
    }
    
    &.btn-secondary {
      background: #f5f5f5;
      color: #666;
      
      &:hover:not(:disabled) {
        background: #e0e0e0;
      }
      
      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
  }
}

// Mobile responsiveness
@media (max-width: 768px) {
  .modal-overlay {
    padding: 10px;
  }
  
  .modal-header, .modal-body, .modal-footer {
    padding: 16px;
  }
  
  .upload-area {
    padding: 30px 15px;
    
    .file-preview {
      flex-direction: column;
      text-align: center;
      
      .file-icon {
        margin-right: 0;
        margin-bottom: 12px;
      }
    }
  }
}
</style>
